import { ReactNode } from 'react';
import { Toaster } from 'src/components/ui/sonner';
import { toast } from 'src/components/ui/toast';

interface ToastOptions {
  message?: string;
  title?: string;
  color?: 'success' | 'info' | 'warning' | 'error';
  /** @deprecated Ignored — toast icons are now determined by type/color. */
  icon?: string;
  persistent?: boolean;
  autoHideDuration?: number;
  onAction?: () => void;
  actionLabel?: string;
  /**
   * Show an icon-only Copy button (top-right) that copies this text. Only
   * rendered on `warning`/`error` toasts.
   */
  copyText?: string;
}

const defaultTitles: Record<string, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Info',
};

/**
 * Resolve how long a toast stays on screen.
 *
 * Precedence:
 *  1. `persistent` (true → never auto-dismiss; false → opt back into a timed
 *     dismissal even for errors).
 *  2. An explicit `autoHideDuration`.
 *  3. Error toasts default to persistent — they stay until the user closes them
 *     with the X. Errors are easy to miss when they vanish after a few seconds,
 *     and the underlying message is often the only clue to what went wrong.
 *  4. Everything else uses Sonner's default timed dismissal.
 */
function resolveDuration(
  color: NonNullable<ToastOptions['color']>,
  persistent: boolean | undefined,
  autoHideDuration: number | undefined,
): number | undefined {
  if (persistent === true) return Infinity;
  if (persistent === false) return autoHideDuration;
  if (autoHideDuration !== undefined) return autoHideDuration;
  if (color === 'error') return Infinity;
  return undefined;
}

function showToast(options: ToastOptions) {
  const {
    message,
    color = 'success',
    persistent,
    autoHideDuration,
    onAction,
    actionLabel,
    copyText,
  } = options;

  const title = options.title ?? defaultTitles[color];

  // Error/warning toasts carry the diagnostic the user most often needs to copy
  // (an error message), so default the copy button to the message when the
  // caller didn't set copyText explicitly.
  const resolvedCopyText =
    copyText ??
    (message && (color === 'error' || color === 'warning')
      ? message
      : undefined);

  toast({
    title,
    description: message,
    type: color,
    duration: resolveDuration(color, persistent, autoHideDuration),
    action:
      onAction && actionLabel
        ? { label: actionLabel, onClick: onAction }
        : undefined,
    copyText: resolvedCopyText,
  });
}

/** @deprecated Kept for backward compat — Toaster no longer needs to wrap children. */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  return (
    <>
      {children}
      <Toaster position="bottom-right" />
    </>
  );
};

export const useToast = () => ({ showToast });
